/* eslint-disable */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

/*import url and gateway methods */
import { getData, postData, putData, uploadSingleFile } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import { useTranslation } from "react-i18next";

import JoditEditor from "jodit-react";

import smtpSecureOptions from "data/Prod/smtpSecureOptions.json";
import { reactSelectStyle } from "Config/Config";
import FileUploadField from "components/Common/FileUploadField/FileUploadField";
import JoditEditorField from "components/Common/JoditEditorField/JoditEditorField";

const SaveEcosystemPopup = ({
  afterPopupCLose,
  ecosystemId,
  setEcosystemId,
}) => {
  const titleRef = useRef(null);

  /* --- Hooks and Context --- */
  const token = localStorage.getItem("token"); // Retrieve authentication token from local storage
  const { t } = useTranslation(); // Hook for internationalization (translations)

  /* --- State Management --- */

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [details, setdetails] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedPreviewImage, setUploadedPreviewImage] = useState("");
  const [uploadedImageId, setUploadedImageId] = useState(null);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpPort, setSmtpPort] = useState("");
  const [smtpSecure, setSmtpSecure] = useState(null);
  const [useSmtpConfigValue, setUseSmtpConfigValue] = useState(false);
  const [useSmtpConfig, setUseSmtpConfig] = useState(null);

  // Form Submission States
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [errors, setErrors] = useState({});

  // Alert States for user feedback
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Interest and Industry states
  const [interestOptions, setInterestOptions] = useState([]);
  const [interestValue, setInterestValue] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [industryValue, setIndustryValue] = useState([]);

  /* --- Form Validation --- */
  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = t(
        "Ecosystem title cannot be empty. Please provide a title.",
      );
    }

    if (!slug.trim()) {
      newErrors.slug = t(
        "Ecosystem slug cannot be empty. Please provide a unique slug.",
      );
    }

    setErrors(newErrors);

    // Auto-scroll to the first error (title) if present
    if (Object.keys(newErrors).length > 0) {
      if (newErrors.name && titleRef.current) {
        setTimeout(() => {
          titleRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          titleRef.current.setAttribute("tabindex", "-1"); // Make element focusable
          titleRef.current.focus();
        }, 100); // Small delay to ensure rendering before scroll
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  /* --- Image Upload Handler --- */
  const imageUploadHandler = async (file) => {
    setIsUploading(true);
    try {
      const requestUrl =
        url.API_BASE_URL + url.API_SINGLE_FILE_UPLOAD;

      const response = await uploadSingleFile(requestUrl, file[0]);

      console.log("upload response-------->", response); // Log upload response for debugging

      if (response.status) {
        setUploadedPreviewImage(response.data.path);
        setUploadedImageId(response.data._id);
        setAlertMessage(t("Image uploaded successfully!"));
        setMessageType("success");
        setShowAlert(true);
      } else {
        // Log specific error message from API response
        console.error("Image upload failed:", response.message);
        setAlertMessage(t("Image upload failed: ") + response.message);
        setMessageType("error");
        setShowAlert(true);
      }
      resetFile(); // Clear the file input value after upload attempt
    } catch (error) {
      // Log network or unexpected errors
      console.error("Error during image upload:", error.message);
      setAlertMessage(t("An error occurred during image upload."));
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsUploading(false); // Ensure loading state is reset regardless of success or failure
    }
  };

  /* --- Reset File Input --- */
  const resetFile = () => {
    const fileInput = document.getElementById("uploadLogoEcosystem");
    if (fileInput) {
      fileInput.value = null; // Clear the selected file to allow re-uploading the same file
    }
  };

  /* --- SMTP Configuration Validation --- */
  const validateSmtpConfig = async () => {
    // Display a temporary alert to indicate the action
    alert(t("Sending test mail and checking connection... (Not implemented)"));
    // TODO: Implement actual SMTP connection test API call and provide visual feedback (success/error)
  };

  /* --- Data Fetching --- */
  const getEcosystemDetails = async () => {
    setIsLoadingDetails(true); // Set loading state to true
    try {
      // Construct the API request URL with ecosystem ID and token
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_ECOSYSTEM_DETAILS +
        `/${ecosystemId}` +
        ``;

      const response = await getData(requestUrl);

      // Log the full response for debugging purposes
      console.log("Ecosystem details response:", response);

      if (response.status) {
        const data = response.data;
        // Update state with fetched data, providing default empty strings or false for null/undefined values
        setName(data.name || "");
        setSlug(data.slug || "");
        setdetails(data.description || "");
        setIsFeatured(data.featured || false);
        setIsDefault(data.isdefault || false);
        setUploadedImageId(data.logo ? data.logo._id : null);
        setUploadedPreviewImage(data.logo ? data.logo.path : "");

        // Populate mail configuration fields, ensuring empty strings for null/undefined
        setSmtpHost(data.smtphost || "");
        setSmtpUser(data.smtpuser || "");
        setSmtpPassword(data.smtppass || "");
        setSmtpPort(data.smtpport || "");

        // Find and set the selected secure SMTP connection option
        const secureOption = smtpSecureOptions.find(
          (option) => option.value === data.smtpsecure,
        );
        setSmtpSecure(secureOption || null);

        // Find and set the option for whether to use this SMTP config
        const useConfigOption = smtpSecureOptions.find(
          (option) => option.value === data.usesmtpmail,
        );
        setUseSmtpConfig(useConfigOption || null);
        setUseSmtpConfigValue(useConfigOption ? useConfigOption.value : false);

        // Set Interest and Industry values
        setInterestValue(
          data.interest
            ? data.interest.map((item) => ({
                label: item,
                value: item,
              }))
            : [],
        );
        setIndustryValue(
          data.industry
            ? data.industry.map((item) => ({
                label: item,
                value: item,
              }))
            : [],
        );
      } else {
        // Log specific error message from API response
        console.error("Failed to fetch ecosystem details:", response.message);
        setAlertMessage(
          t("Failed to load ecosystem details: ") + response.message,
        );
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (error) {
      // Log network or unexpected errors during data fetching
      console.error("Error fetching ecosystem details:", error.message);
      setAlertMessage(t("An error occurred while fetching ecosystem details."));
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsLoadingDetails(false); // Ensure loading state is reset regardless of success or failure
    }
  };

  /* --- Save/Update Handler --- */
  const saveHandler = async () => {
    // Validate form fields before proceeding with save operation
    if (!validateForm()) {
      // If validation fails, error messages are already set by `validateForm`
      setAlertMessage(
        t("Please correct the highlighted fields before saving."),
      );
      setMessageType("error");
      setShowAlert(true);
      return;
    }
    setIsSaving(true); // Set saving state to true to show loading indicator
    try {
      // Generate a random secret. Review if this 'secret' is truly needed client-side
      // or if it should be generated and managed exclusively on the server.
      const randomNum = Math.random() * 1000;

      // Construct the data payload for the API request
      const sendingData = {
        name,
        slug,
        secret: randomNum.toString(), // This might be better handled server-side
        description: details,
        featured: isFeatured,
        isdefault: isDefault,
        logo: uploadedImageId,
        // Trim and validate SMTP fields, sending empty strings if not sufficiently long
        smtphost: smtpHost.trim().length > 2 ? smtpHost.trim() : "",
        smtpuser: smtpUser.trim().length > 2 ? smtpUser.trim() : "",
        smtppass: smtpPassword.trim().length > 2 ? smtpPassword.trim() : "",
        smtpport: smtpPort.trim().length > 1 ? smtpPort.trim() : "",
        smtpsecure: smtpSecure ? smtpSecure.value : false, // Send boolean value
        usesmtpmail: useSmtpConfig ? useSmtpConfig.value : false, // Send boolean value
        interest: interestValue.map((item) => item.value),
        industry: industryValue.map((item) => item.value),
      };

      // Log the data being sent for debugging
      console.log("Sending data:", sendingData);

      let requestUrlEndpoint;
      let response;

      // Determine whether to create or update based on `ecosystemId` presence
      if (ecosystemId) {
        // Update existing ecosystem: construct PUT request URL
        requestUrlEndpoint =
          url.API_UPDATE_ECOSYSTEM + `/${ecosystemId}`;
        response = await putData(
          url.API_BASE_URL + requestUrlEndpoint,
          sendingData,
        );
      } else {
        // Create new ecosystem: construct POST request URL
        requestUrlEndpoint = url.API_ADD_ECOSYSTEM;
        response = await postData(
          url.API_BASE_URL + requestUrlEndpoint,
          sendingData,
        );
      }

      // Log the API response for debugging
      console.log("API Response:", response);

      if (response.status) {
        // On successful save, hide the offcanvas modal
        if (window.bootstrap && window.bootstrap.Offcanvas) {
          const offcanvasElement = document.querySelector("#saveEcosystem");
          const offcanvasInstance =
            window.bootstrap.Offcanvas.getInstance(offcanvasElement);
          if (offcanvasInstance) {
            offcanvasInstance.hide();
          }
        }
        resetHandler(); // Reset all form fields to initial state
        afterPopupCLose(response.data); // Execute the callback function provided by the parent component
        setAlertMessage(t("Ecosystem saved successfully!"));
        setMessageType("success");
        setShowAlert(true);
      } else {
        // Log specific error message from API response
        console.error("Save operation failed:", response.message);
        setAlertMessage(t("Failed to save ecosystem: ") + response.message);
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (error) {
      // Log network or unexpected errors during the save operation
      console.error("Error saving ecosystem:", error.message);
      setAlertMessage(t("An error occurred while saving the ecosystem."));
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsSaving(false); // Ensure saving state is reset regardless of success or failure
    }
  };

  /* --- Reset Handler --- */
  const resetHandler = () => {
    resetFile(); // Clear the file input
    setName("");
    setSlug("");
    setdetails("");
    setIsFeatured(false);
    setIsDefault(false);
    setUploadedImageId(null);
    setUploadedPreviewImage("");
    setEcosystemId(null); // Reset the ecosystem ID in the parent component
    setSmtpHost("");
    setSmtpUser("");
    setSmtpPassword("");
    setSmtpPort("");
    setSmtpSecure(null);
    setUseSmtpConfigValue(false);
    setUseSmtpConfig(null);
    setInterestValue([]);
    setIndustryValue([]);
    setErrors({}); // Clear any validation errors
    setIsSaving(false); // Ensure saving state is false
    setIsLoadingDetails(false); // Ensure loading state for details is false
    setShowAlert(false); // Hide any active alerts
    setAlertMessage(""); // Clear alert message
    setMessageType(""); // Clear alert type
  };

  /* --- Effects --- */
  useEffect(() => {
    if (ecosystemId) {
      getEcosystemDetails(); // Fetch details if in edit mode
    } else {
      resetHandler(); // Reset form if switching to create mode or initial load without ID
    }
  }, [ecosystemId]); // Dependencies for the effect

  // Handler for Interest creatable select
  const handleInterestChange = (val) => {
    setInterestValue(val);
    setInterestOptions((prev) => {
      const newOptions = val.filter(
        (item) => !prev.some((opt) => opt.value === item.value),
      );
      return [...prev, ...newOptions];
    });
  };

  // Handler for Industry creatable select
  const handleIndustryChange = (val) => {
    setIndustryValue(val);
    setIndustryOptions((prev) => {
      const newOptions = val.filter(
        (item) => !prev.some((opt) => opt.value === item.value),
      );
      return [...prev, ...newOptions];
    });
  };

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveEcosystem"
      aria-labelledby="saveEcosystem"
    >
      {/* Header design similar to SaveFeedbackRulePopup */}
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              eco
            </span>{" "}
            {/* Using 'eco' icon as a placeholder */}
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasLabelDetailsLabel"
            >
              {ecosystemId ? t("Edit Ecosystem") : t("Create Ecosystem")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {ecosystemId
                ? t("Manage existing ecosystem details")
                : t("Define a new ecosystem")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4">
        {isLoadingDetails && (
          <div className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-index-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                {t("Loading ecosystem details...")}
              </span>
            </div>
            <p className="text-primary mt-3 fw-bold">
              {t("Loading ecosystem details...")}
            </p>
          </div>
        )}
        {/* Professional Card Layout */}
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
          <form
            onSubmit={(e) => e.preventDefault()}
            className="d-flex flex-column h-100"
          >
            <div className="fields-container flex-fill">
              {/* Title */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="labelName"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Title")} <span className="text-danger">*</span>
                </label>
                <input
                  ref={titleRef}
                  type="text"
                  id="labelName"
                  className={`form-control fs-sm shadow-none ${
                    errors.name ? "is-invalid" : "border border-gray-300"
                  }`}
                  placeholder={t(
                    "Enter the ecosystem title, e.g., 'Global Innovation Hub'",
                  )}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) {
                      setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  aria-describedby="titleFeedback"
                />
                {errors.name && (
                  <div id="titleFeedback" className="invalid-feedback">
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Slug */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="slugID"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Slug")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="slug"
                  className={`form-control fs-sm shadow-none ${
                    errors.slug ? "is-invalid" : "border border-gray-300"
                  }`}
                  placeholder={t(
                    "Enter a unique slug, e.g., 'global-innovation-hub'",
                  )}
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    if (errors.slug) {
                      setErrors((prev) => ({ ...prev, slug: "" }));
                    }
                  }}
                  aria-describedby="slugFeedback"
                />
                {errors.slug && (
                  <div id="slugFeedback" className="invalid-feedback">
                    {errors.slug}
                  </div>
                )}
              </div>

              {/* Details Editor */}
              <div className="form-group mb-4 p-3 pb-0 border rounded-8 shadow-sm bg-white">
                <JoditEditorField
                  label={t("Details")}
                  value={details}
                  onChange={(newContent) => setdetails(newContent)}
                  labelClassName="d-block fs-sm fw-semibold mb-2 text-dark"
                />
              </div>

              {/* Interest Multiselect */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="interest"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Interest")}
                </label>
                <CreatableSelect
                  isMulti
                  placeholder={t("Type or create interests")}
                  options={interestOptions}
                  value={interestValue}
                  onChange={handleInterestChange}
                  styles={reactSelectStyle("3px", "6px", true, false)}
                />
              </div>
              {/* Industry Multiselect */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="industry"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Industry")}
                </label>
                <CreatableSelect
                  isMulti
                  placeholder={t("Type or create industries")}
                  options={industryOptions}
                  value={industryValue}
                  onChange={handleIndustryChange}
                  styles={reactSelectStyle("3px", "6px", true, false)}
                />
              </div>

              {/* Featured and Default Checkboxes */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="form-check d-flex align-items-center gap-2 fs-sm p-0 m-0">
                      <input
                        className="form-check-input bg-transparent border-2 border-gray-600 shadow-none m-0"
                        type="checkbox"
                        id="isFeatured"
                        checked={isFeatured}
                        onChange={(e) => setIsFeatured(e.target.checked)}
                      />
                      <label
                        className="form-check-label fw-semibold text-dark"
                        htmlFor="isFeatured"
                      >
                        {t("Featured")}
                      </label>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="form-check d-flex align-items-center gap-2 fs-sm p-0 m-0">
                      <input
                        className="form-check-input bg-transparent border-2 border-gray-600 shadow-none m-0"
                        type="checkbox"
                        id="isDefault"
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                      />
                      <label
                        className="form-check-label fw-semibold text-dark"
                        htmlFor="isDefault"
                      >
                        {t("Is Default")}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                <label
                  htmlFor="logoUpload"
                  className="d-block fs-sm fw-semibold mb-2 text-dark"
                >
                  {t("Logo")}
                </label>

                <FileUploadField
                  label={t("Upload Logo")}
                  accept="jpg png jpeg"
                  onUpload={imageUploadHandler}
                  files={
                    uploadedPreviewImage === ""
                      ? [
                          {
                            path: "https://placehold.co/150x150",
                            name: "Logo",
                          },
                        ]
                      : [
                          {
                            path: url.SERVER_URL + uploadedPreviewImage,
                            name: "Logo",
                          },
                        ]
                  }
                  onRemove={() => {
                    setUploadedPreviewImage("");
                    setUploadedImageId(null);
                  }}
                  loading={isUploading}
                  fileType="image"
                />
              </div>

              {/* Mail sending configuration section */}
              <div className="mt-4 mb-3 text-primary fw-semibold fs-md d-flex align-items-center gap-2">
                <span className="material-symbols-outlined icon-lg">mail</span>
                <span>{t("Send Mail Configuration (for sending mails)")}</span>
              </div>

              <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <label
                      htmlFor="smtpHost"
                      className="fs-md fw-semibold text-dark"
                    >
                      {t("SMTP Host")}
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <input
                      type="text"
                      id="smtpHost"
                      className="form-control fs-sm shadow-none border border-gray-300"
                      placeholder={t("e.g., smtp.example.com")}
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <label
                      htmlFor="smtpUser"
                      className="fs-md fw-semibold text-dark"
                    >
                      {t("SMTP User")}
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <input
                      type="text"
                      id="smtpUser"
                      className="form-control fs-sm shadow-none border border-gray-300"
                      placeholder={t("e.g., your_email@example.com")}
                      value={smtpUser}
                      onChange={(e) => setSmtpUser(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <label
                      htmlFor="smtpPassword"
                      className="fs-md fw-semibold text-dark"
                    >
                      {t("SMTP Password")}
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <input
                      type="password"
                      id="smtpPassword"
                      className="form-control fs-sm shadow-none border border-gray-300"
                      placeholder={t("Enter your SMTP password")}
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <label
                      htmlFor="smtpPort"
                      className="fs-md fw-semibold text-dark"
                    >
                      {t("SMTP Port")} (
                      <i>
                        {t("587 for non-SSL")}, {t("465 for SSL")}
                      </i>
                      )
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <input
                      type="text"
                      id="smtpPort"
                      className="form-control fs-sm shadow-none border border-gray-300"
                      placeholder={t("e.g., 587 or 465")}
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <label
                      htmlFor="smtpSecure"
                      className="fs-md fw-semibold text-dark"
                    >
                      {t("Is secure connection?")} (
                      <i>{t("true if SSL/TLS")}</i>)
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <Select
                      isClearable
                      styles={reactSelectStyle("3px", "6px", true, false)}
                      placeholder={t(
                        "Select if connection is secure (SSL/TLS)",
                      )}
                      options={smtpSecureOptions}
                      value={smtpSecure}
                      onChange={(val) => {
                        setSmtpSecure(val);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                <div className="row align-items-center">
                  <div className="col-lg-6 mb-3 mb-sm-4 mb-lg-0">
                    <label
                      htmlFor="useSmtpConfig"
                      className="fs-md fw-semibold text-dark"
                    >
                      {t("Use this configuration to send mails")}
                    </label>
                  </div>
                  <div className="col-lg-6">
                    <Select
                      placeholder={t(
                        "Choose whether to use this SMTP configuration",
                      )}
                      styles={reactSelectStyle("3px", "6px", true, false)}
                      options={smtpSecureOptions}
                      value={useSmtpConfig}
                      onChange={(val) => {
                        setUseSmtpConfig(val);
                        setUseSmtpConfigValue(val ? val.value : false);
                      }}
                    />
                  </div>

                  <div className="col-lg-12 mt-3 text-end">
                    {useSmtpConfigValue === true && (
                      <button
                        type="button"
                        className="btn btn-outline-success d-flex align-items-center gap-2 ms-auto"
                        onClick={validateSmtpConfig}
                      >
                        <span className="material-symbols-outlined me-1">
                          check_circle
                        </span>
                        {t("Check Connection")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* New offcanvas footer with sticky positioning */}
      <div
        className="offcanvas-footer p-3 bg-white border-top d-flex justify-content-end"
        style={{ position: "sticky", bottom: 0, zIndex: 10 }}
      >
        <button
          onClick={saveHandler}
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          disabled={isSaving || isLoadingDetails}
          style={{
            cursor: isSaving || isLoadingDetails ? "not-allowed" : "pointer",
          }}
        >
          {isSaving ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {t("Saving...")}
            </>
          ) : (
            <>
              <span className="material-symbols-outlined me-1">save</span>{" "}
              {t("Save Ecosystem")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SaveEcosystemPopup;
